/*
 * TransactionError.tsx (AbstractECommerce)
 *
 * Copyright © 2020 InstaLOD GmbH - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * This file and all its contents are proprietary and confidential.
 *
 * Maintained by Martin Witczak, 2020
 *
 * @file TransactionError.tsx
 * @author Martin Witczak
 * @copyright 2020 InstaLOD GmbH. All rights reserved.
 * @section License
 */

import { Message } from 'primereact/message';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

/**
 * Interface for TransactionError properties.
 */
interface ITransactionErrorProperties {
  error: {
    status?: string;
    reasons?: {
      processorResponseCode?: any;
      processorResponseText?: string;
      gatewayRejectionReason?: string;
      [key: string]: any /**< Dynamic properties */;
    };
    errorId?: any;
    [key: string]: any /**< Dynamic properties */;
  };
}

const TransactionError = ({ error }: ITransactionErrorProperties) => {
  const shopSettings = useSelector((state) => state.shopSettings.list);
  const contactEmail: string = shopSettings?.smtpEmail ?? ''; /**< SMTP email */

  const { t } = useTranslation();

  if (!error) {
    return null;
  }

  if (error.status === 'processor_declined') {
    return (
      <Message
        text={t(`checkoutPage.paymentErrors.processor_declined`, {
          error: error.reasons.processorResponseCode,
          errorText: error.reasons.processorResponseText,
          contactEmail,
          errorId: error.errorId
        })}
        severity="error"
      />
    );
  }

  if (error.status === 'gateway_rejected') {
    return (
      <Message
        text={t(`checkoutPage.paymentErrors.gateway_rejected`, {
          errorText: error.reasons.gatewayRejectionReason,
          contactEmail,
          errorId: error.errorId
        })}
        severity="error"
      />
    );
  }

  if (error.status === 'form_error') {
    return <Message text={t(`checkoutPage.formError`)} severity="error" />;
  }

  return (
    <Message
      text={t(
        [
          `checkoutPage.paymentErrors.${error.status}`,
          'checkoutPage.paymentErrors.default_error'
        ],
        { contactEmail, errorId: error.errorId }
      )}
      severity="error"
    />
  );
};

export default TransactionError;
